import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getRunHistoryData,
  getRunHistoryLoadingState,
} from "ee/selectors/workflowRunHistorySelectors";
import { SegmentedControl } from "@appsmith/ads";
import styled from "styled-components";
import { fetchWorkflowRunHistory } from "ee/actions/workflowRunHistoryActions";
import { RunHistoryListItem } from "./RunHistoryListItem";
import { HistoryStateFilterStates } from "./helpers";
import type { AppState } from "ee/reducers";
import {
  createMessage,
  LOADING,
  WORKFLOW_RUN_HISTORY_TAB_LABEL_ALL_RUNS,
  WORKFLOW_RUN_HISTORY_TAB_LABEL_FAILED_RUNS,
} from "ee/constants/messages";

const RunHistoryListContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  gap: 4px;
  flex-grow: 1;
`;

const StyledSegmentedControl = styled(SegmentedControl)`
  margin: 8px;
  width: 250px;
`;

interface RunHistoryListProps {
  selectedRunId: string;
  setSelectedRunId: (runId: string) => void;
  workflowId: string;
}

export function RunHistoryList({
  selectedRunId,
  setSelectedRunId,
  workflowId,
}: RunHistoryListProps) {
  const dispatch = useDispatch();
  const isLoading = useSelector(getRunHistoryLoadingState);
  const data = useSelector((state: AppState) =>
    getRunHistoryData(state, workflowId),
  );
  const [selectedFilter, setSelectedFilter] = useState(
    HistoryStateFilterStates.ALL_RUNS,
  );

  useEffect(() => {
    dispatch(fetchWorkflowRunHistory(workflowId, selectedFilter));
  }, [dispatch, selectedFilter, workflowId]);
  useEffect(() => {
    if (data.length) {
      setSelectedRunId(data[0].workflowRunId);
    }
  }, [data, setSelectedRunId]);

  return (
    <>
      <StyledSegmentedControl
        aria-disabled={isLoading}
        onChange={(value) => {
          if (isLoading) return;

          setSelectedFilter(value);
        }}
        options={[
          {
            label: createMessage(WORKFLOW_RUN_HISTORY_TAB_LABEL_ALL_RUNS),
            value: HistoryStateFilterStates.ALL_RUNS,
          },
          {
            label: createMessage(WORKFLOW_RUN_HISTORY_TAB_LABEL_FAILED_RUNS),
            value: HistoryStateFilterStates.FAILED_RUNS,
          },
        ]}
        value={selectedFilter}
      />
      {isLoading ? (
        <div>{createMessage(LOADING)}</div>
      ) : (
        <RunHistoryListContainer data-testid="t--run-history-list">
          {data.map((run) => (
            <RunHistoryListItem
              data={run}
              key={run.workflowRunId}
              onClick={() => setSelectedRunId(run.workflowRunId)}
              selected={run.workflowRunId === selectedRunId}
            />
          ))}
        </RunHistoryListContainer>
      )}
    </>
  );
}
